import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../../../context/AuthContext';
import {
  saveSafetyResultSummary,
  deleteSafetyResultSummary,
  getSafetyResultSummary
} from '../../../../api/safetyAssessApi';

import SafetyApprovalTab from './report/SafetyApprovalTab';
import SafetyMeetingTab from './report/SafetyMeetingTab';
import SafetyTrainingTab from './report/SafetyTrainingTab';
import SafetySummaryTab from './report/SafetySummaryTab';

function SafetyMeetingTrainingForm({
  assessmentId, summaryData, setSummaryData, projectInfo, syncProjectInfo, projectStatus, onOpenApprovalModal,
  isHeaderVisible, onToggleHeader
}) {
  const { user, token } = useContext(AuthContext);
  const [activeSubTab, setActiveSubTab] = useState('TRAINING');

  // 프로젝트 결재 락 조건 판단 (실제 결재진행중/결재완료/승인완료인 경우만 락)
  const isLocked = projectStatus && ['결재 진행 중', '결재진행중', '결재 완료', '결재완료', '승인 완료', '승인완료', 'approved'].includes(projectStatus);

  // 마운트 및 assessmentId 변경 시 최신 총평 정보 조회
  useEffect(() => {
    const fetchLatestSummary = async () => {
      if (!assessmentId || !token) return;
      try {
        const summary = await getSafetyResultSummary(assessmentId, token);
        if (summary && summary.id) {
          setSummaryData({
            id: summary.id,
            writerId: summary.writerId || '',
            writer: summary.writer || '',
            writeDate: summary.writeDate ? summary.writeDate.split('T')[0] : '',
            conclusion: summary.conclusion || '',
            files: summary.files || []
          });
        }
      } catch (err) {
        console.error('fetchLatestSummary error:', err);
      }
    };
    fetchLatestSummary();
  }, [assessmentId, token, setSummaryData]);

  // 회의록/교육일지/총평 저장 및 삭제 핸들러
  const handleSave = async (fieldGroup, customSummaryData) => {
    // 락이 걸렸더라도 admin은 저장 가능하도록 허용
    if (isLocked && user?.role !== 'admin') return alert('결재가 진행 중이거나 승인 완료된 문서는 수정할 수 없습니다.');
    try {
      if (fieldGroup === 'summary_delete') {
        // 실제 DB 삭제(DELETE) API 호출
        await deleteSafetyResultSummary(assessmentId, token);
        alert('종합 총평이 성공적으로 삭제되었습니다.');
        setSummaryData({
          id: null,
          writerId: '',
          writer: '',
          writeDate: '',
          conclusion: '',
          files: []
        });
      } else {
        const dataToSave = customSummaryData || summaryData;
        await saveSafetyResultSummary({ ...dataToSave, assessmentId }, token);
        alert('종합 총평이 성공적으로 저장되었습니다.');

        // 최신 정보로 리로드
        const summary = await getSafetyResultSummary(assessmentId, token);
        if (summary && summary.id) {
          setSummaryData({
            id: summary.id,
            writerId: summary.writerId || dataToSave.writerId || '',
            writer: summary.writer || dataToSave.writer || '',
            writeDate: summary.writeDate ? summary.writeDate.split('T')[0] : (dataToSave.writeDate || ''),
            conclusion: summary.conclusion || '',
            files: summary.files || dataToSave.files || []
          });
        }
      }
    } catch (err) {
      console.error('handleSave Error:', err);
      alert('처리 실패');
    }
  };


  // 서브 탭 정의
  const subTabs = [
    { key: 'TRAINING', label: '교육일지', icon: 'bi bi-journal-check' },
    { key: 'MEETING', label: '회의록', icon: 'bi bi-chat-left-quote' },
    { key: 'SUMMARY', label: '종합총평', icon: 'bi bi-file-earmark-text' },
    { key: 'APPROVAL', label: '결재', icon: 'bi bi-check2-square' }
  ];

  return (
    <div className="sf-animate">
      {/* 서브 탭 헤더 바 (세그먼트/폴더형) */}
      <div className="mt-2 z-1">
        <div className="d-flex align-items-end" style={{ borderBottom: '1px solid #ced4da' }}>
          {subTabs.map((tab, index) => {
            const isActive = activeSubTab === tab.key;
            return (
              <div
                key={tab.key}
                className={`px-4 py-2 d-flex align-items-center ${isActive ? 'fw-bold text-primary' : 'fw-semibold text-dark'}`}
                onClick={() => setActiveSubTab(tab.key)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isActive ? '#ffffff' : '#e9ecef',
                  borderTop: isActive ? '3px solid #0d6efd' : '1px solid #ced4da',
                  borderLeft: '1px solid #ced4da',
                  borderRight: '1px solid #ced4da',
                  borderBottom: isActive ? '1px solid #ffffff' : '1px solid #ced4da',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  marginBottom: '-1px', // 컨테이너의 bottom border 덮기
                  fontSize: '0.9rem',
                  minWidth: '120px',
                  justifyContent: 'center',
                  position: 'relative',
                  bottom: '-1px',
                  transition: 'all 0.2s ease',
                  marginLeft: index > 0 ? '-1px' : '0',
                  color: isActive ? '#0d6efd' : '#495057'
                }}
              >
                <i className={`${tab.icon} me-2`} style={{ fontSize: '1.1rem' }}></i>
                {tab.label}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}></div>

      {/* 서브 탭 콘텐츠 */}
      {activeSubTab === 'APPROVAL' && <SafetyApprovalTab projectInfo={projectInfo} onOpenApprovalModal={onOpenApprovalModal} />}
      {activeSubTab === 'MEETING' && (
        <SafetyMeetingTab
          assessmentId={assessmentId}
          summaryData={summaryData}
          setSummaryData={setSummaryData}
          isLocked={isLocked}
          onSave={handleSave}
          projectInfo={projectInfo}
          onOpenApprovalModal={onOpenApprovalModal}
        />
      )}
      {activeSubTab === 'SUMMARY' && (
        <SafetySummaryTab
          assessmentId={assessmentId}
          summaryData={summaryData}
          setSummaryData={setSummaryData}
          isLocked={isLocked}
          onSave={handleSave}
          projectInfo={projectInfo}
        />
      )}
      {activeSubTab === 'TRAINING' && (
        <SafetyTrainingTab
          assessmentId={assessmentId}
          summaryData={summaryData}
          setSummaryData={setSummaryData}
          isLocked={isLocked}
          onSave={handleSave}
          projectInfo={projectInfo}
        />
      )}
    </div>
  );
}

export default SafetyMeetingTrainingForm;
